import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import FilterListIcon from '@mui/icons-material/FilterList'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'

export type ExpertisesSearchFormValues = {
  doctor: string
  patient: string
  type: string
  startDate: {
    $gte: Date | null
    $lte: Date | null
  }
}

export type ExpertisesSearchFormProps = {
  doctorOptions: AutocompleteOption[]
  onReset?: (values: ExpertisesSearchFormValues) => void

  onSearchDoctor?: (value: string) => void
  onSearchPatient?: (value: string) => void
  onSubmit: (values: ExpertisesSearchFormValues) => void
  patientOptions: AutocompleteOption[]
}

export default function ExpertisesSearchFormMobile(props: ExpertisesSearchFormProps) {
  const { doctorOptions, onReset, onSearchDoctor, onSearchPatient, onSubmit, patientOptions } = props
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'advanced-filters-popover' : undefined

  const handleSearchDoctor = useCallback(
    (inputValue: string) => {
      onSearchDoctor?.(inputValue)
    },
    [onSearchDoctor]
  )

  const handleSearchPatient = useCallback(
    (inputValue: string) => {
      onSearchPatient?.(inputValue)
    },
    [onSearchPatient]
  )

  return (
    <Box sx={{ padding: '0 15px' }}>
      <Formik
        initialValues={{
          doctor: '',
          patient: '',
          type: '',
          startDate: {
            $gte: dayjs().startOf('day').toDate(),
            $lte: null,
          },
        }}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Grid
              container
              spacing={2}
              sx={{
                marginTop: '10px',
              }}
            >
              <Grid item xs={10}>
                <AutocompleteField
                  label={t('page.expertises.expertisesSearchForm.field.searchByPatient.label')}
                  name="patient"
                  onInputChange={handleSearchPatient}
                  options={patientOptions}
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton onClick={handleClick}>
                  <FilterListIcon />
                </IconButton>
              </Grid>
              <Menu
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem>
                  <AutocompleteField
                    label={t('page.expertises.expertisesSearchForm.field.searchByDoctor.label')}
                    name="doctor"
                    onInputChange={handleSearchDoctor}
                    options={doctorOptions}
                    fullWidth
                  />
                </MenuItem>
                <MenuItem>
                  <DatePickerField fullWidth label={t('page.expertises.expertisesSearchForm.field.startDateFrom.label')} name="startDate.$gte" />
                </MenuItem>
                <MenuItem>
                  <DatePickerField fullWidth label={t('page.expertises.expertisesSearchForm.field.startDateTo.label')} name="startDate.$lte" />
                </MenuItem>
              </Menu>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    onClick={() => {
                      formikProps.handleReset()
                      onReset?.(formikProps.initialValues)
                    }}
                    variant="neutral"
                  >
                    {t('common:button.reset.label')}
                  </Button>
                  <LoadingButton id="searchButton" type="submit" variant="contained">
                    {t('common:button.submit.label')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
